import * as React from 'react';

import {Field} from 'redux-form';
// @ts-expect-error
import type {FieldProps} from 'redux-form';

import {EmptyCircle} from 'shells/list-sequence/empty-circle';
import {ListSequence} from 'shells/list-sequence/list-sequence';
import {ListSequenceRowBody} from 'shells/list-sequence/list-sequence-row-body';
import {LoadingText} from 'shells/loading-placeholder/loading-text';

import {EmailDelay} from './email-delay';
import {getTemplateFormStateName} from './get-template-form-state-name';
import type {EmailComposerFormTemplate} from './types';

import styles from './email-template-list.module.css';

const NO_SUBJECT = 'No subject';
const NO_BODY = 'No email body';

type Props = {
    sequenceId: string;
    templates: EmailComposerFormTemplate[];
    selectedTemplateId: string;
    onTemplateClick: (templateId: string) => void;
    noPadding?: boolean;
    isLoading?: boolean;
};

export class EmailTemplateList extends React.PureComponent<Props> {
    render() {
        return (
            <div
                style={this.props.noPadding ? {padding: '0'} : undefined}
                className={styles['email-template-list']}
            >
                <ListSequence
                    collection={this.props.templates}
                    selectedRowId={this.props.selectedTemplateId}
                    renderRow={this.renderRow}
                />
            </div>
        );
    }

    renderRow = (rowProps: {isSelected: boolean; row: EmailComposerFormTemplate}) => {
        const {isSelected, row: template} = rowProps;

        const hasDelay = template.delay && template.delay.timespan !== 0;

        const templateStateName = getTemplateFormStateName({
            sequenceId: this.props.sequenceId,
            templateId: template.id,
        });

        return {
            icon: hasDelay ? <EmptyCircle offsetTop={32} /> : undefined,
            body: (
                <div className={styles.body}>
                    {hasDelay ? (
                        <EmailDelay
                            delay={template.delay}
                            expectedSendTime={template.expectedSendTime}
                        />
                    ) : null}
                    <ListSequenceRowBody
                        isSelected={isSelected}
                        onClick={this.handleRowSelect(template)}
                        title={
                            <Field
                                name={`${templateStateName}.subject`}
                                component={EmailSubject}
                                isLoading={this.props.isLoading}
                            />
                        }
                        warningMessage={template.missingFieldsMessage}
                        /*
                           Even though we could just render the template.plaintextBody directly here,
                           we are using a redux-form Field component, so that the plaintextBody field
                           is registered with redux-form, and can therefore be changed in our
                           re-initialization when we materialize the templates.
                        */
                        subtitle={
                            <Field
                                name={`${templateStateName}.plaintextBody`}
                                component={EmailBodyPreview}
                                validate={[requiredPlaintextBody]}
                                isLoading={this.props.isLoading}
                            />
                        }
                    />
                </div>
            ),
        };
    };

    handleRowSelect = (template: EmailComposerFormTemplate) => () => {
        this.props.onTemplateClick(template.id);
    };
}

// @ts-expect-error
const requiredPlaintextBody = (value) =>
    !value || value.trim().length === 0 ? 'What should this email say?' : undefined;

type ComponentProps = FieldProps & {
    isLoading: boolean;
};

const EmailBodyPreview: (...args: any) => any = (props: ComponentProps) => {
    const {input, meta, isLoading} = props;
    if (isLoading && !input.value) {
        return <LoadingText width={90} />;
    }

    let body = input.value;

    if (meta.error) {
        body = NO_BODY;
    }

    if (meta.error && meta.submitFailed) {
        return <span className={styles['error-text']}>{body}</span>;
    }

    return body;
};

const EmailSubject: (...args: any) => any = (props: ComponentProps) => {
    const {input, meta, isLoading} = props;
    if (isLoading && !input.value) {
        return <LoadingText width={60} />;
    }

    let subject = input.value;

    if (meta.error) {
        subject = NO_SUBJECT;
    }

    if (meta.error && meta.submitFailed) {
        return <span className={styles['error-text']}>{subject}</span>;
    }

    return subject;
};
